import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ProductCard from '../components/ProductCard';
import { getProdutos } from '../services/api';
import './Produtos.css';

const ITEMS_PER_PAGE = 12;
const SORT_OPTIONS = [
  { value: 'recentes', label: 'Mais recentes' },
  { value: 'preco_asc', label: 'Menor preço' },
  { value: 'preco_desc', label: 'Maior preço' },
  { value: 'nome', label: 'Nome A-Z' },
];

const Produtos = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoriaFromUrl = searchParams.get('categoria') || '';

  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState(categoriaFromUrl);
  const [sortOrder, setSortOrder] = useState('recentes');
  const [currentPage, setCurrentPage] = useState(1);

  // Sincronizar categoria da URL com o estado (ex.: ao clicar em uma categoria no carrossel)
  useEffect(() => {
    setSelectedCategoria(categoriaFromUrl);
  }, [categoriaFromUrl]);

  useEffect(() => {
    loadProdutos();
  }, []);

  const loadProdutos = async () => {
    try {
      const response = await getProdutos();
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setProdutos(data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      setLoading(false);
    }
  };

  // Nome da categoria do produto (vindo do join categoria_nome ou legado categoria)
  const getCategoriaNome = (p) => (p.categoria_nome || p.categoria || '').trim();

  // Obter categorias únicas para o filtro lateral
  const categorias = [...new Set(produtos.map(getCategoriaNome).filter(Boolean))];

  // Filtrar produtos: busca + categoria (ao clicar na categoria, listar só os daquela categoria)
  const normalizeCat = (s) => (s || '').trim().toLowerCase();
  let filteredProdutos = produtos.filter(produto => {
    const matchSearch = produto.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (produto.descricao && produto.descricao.toLowerCase().includes(searchTerm.toLowerCase()));
    const catNome = getCategoriaNome(produto);
    const matchCategoria = !selectedCategoria || normalizeCat(catNome) === normalizeCat(selectedCategoria);
    return matchSearch && matchCategoria;
  });

  // Ordenar
  const sorted = [...filteredProdutos].sort((a, b) => {
    if (sortOrder === 'recentes') {
      const da = a.created_at ? new Date(a.created_at) : new Date(0);
      const db = b.created_at ? new Date(b.created_at) : new Date(0);
      return db - da;
    }
    if (sortOrder === 'preco_asc') return parseFloat(a.preco) - parseFloat(b.preco);
    if (sortOrder === 'preco_desc') return parseFloat(b.preco) - parseFloat(a.preco);
    if (sortOrder === 'nome') return (a.nome || '').localeCompare(b.nome || '');
    return 0;
  });

  // Paginação
  const totalPages = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE));
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProdutos = sorted.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategoria, sortOrder]);

  return (
    <div className="produtos-page">
      <Helmet>
        <title>Nossos Produtos | Loucas por Beleza</title>
        <meta name="description" content="Confira todos os produtos de beleza da Loucas por Beleza. Maquiagem, skincare, cabelos e mais." />
      </Helmet>
      <div className="container">
        <div className="produtos-header">
          <h1 className="page-title">
            {selectedCategoria ? `Produtos em ${selectedCategoria}` : 'Nossos Produtos'}
          </h1>
          <div className="produtos-header-actions">
            <span className="results-count">
              {filteredProdutos.length} {filteredProdutos.length === 1 ? 'produto encontrado' : 'produtos encontrados'}
            </span>
            <label className="sort-label">
              Ordenar:
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="sort-select"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        
        <div className="produtos-layout">
          {/* Sidebar de Filtros */}
          <aside className="filters-sidebar">
            <div className="filter-section">
              <h3 className="filter-title">Buscar</h3>
              <input
                type="text"
                placeholder="Buscar produtos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {categorias.length > 0 && (
              <div className="filter-section">
                <h3 className="filter-title">Categorias</h3>
                <div className="filter-options">
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="categoria"
                      value=""
                      checked={selectedCategoria === ''}
                      onChange={(e) => setSelectedCategoria(e.target.value)}
                    />
                    <span>Todas</span>
                  </label>
                  {categorias.map(categoria => (
                    <label key={categoria} className="filter-option">
                      <input
                        type="radio"
                        name="categoria"
                        value={categoria}
                        checked={selectedCategoria === categoria}
                        onChange={(e) => setSelectedCategoria(e.target.value)}
                      />
                      <span>{categoria}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {selectedCategoria && (
              <button 
                className="clear-filters"
                onClick={() => {
                  setSelectedCategoria('');
                  setSearchParams({});
                }}
              >
                Limpar filtros
              </button>
            )}
          </aside>

          {/* Área de Produtos */}
          <main className="produtos-main">
            {loading ? (
              <div className="loading">Carregando produtos...</div>
            ) : filteredProdutos.length > 0 ? (
              <>
                <div className="products-grid-mercadolivre">
                  {paginatedProdutos.map(produto => (
                    <ProductCard key={produto.id} produto={produto} />
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      type="button"
                      className="pagination-btn"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      Anterior
                    </button>
                    <span className="pagination-info">
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      type="button"
                      className="pagination-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-products">
                {searchTerm || selectedCategoria 
                  ? 'Nenhum produto encontrado para sua busca.' 
                  : 'Nenhum produto cadastrado ainda.'}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Produtos;

